/**
 * Pending Invites Hook - Issue #907
 *
 * React Query hooks for managing pending club invites
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  invitesApi,
  type PendingInvite,
  type PendingInvitesResponse,
} from '../lib/api/invites';
import { useOroMutation, OroOperation } from './useOroMutation';
import { useAuth } from './useAuth';

// Re-export types for convenience
export type { PendingInvite, PendingInvitesResponse };

/**
 * Get pending invites for the current user
 */
export function usePendingInvites() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['pending-invites'],
    queryFn: () => invitesApi.getPendingInvites(),
    enabled: !!user,
    select: (data) => data.invites,
  });
}

/**
 * Accept a pending invite
 */
export function useAcceptInvite() {
  const queryClient = useQueryClient();

  return useOroMutation<{ message: string; club_id: string; club_slug: string; role?: string }, string>({
    mutationFn: (code: string) => invitesApi.acceptInvite(code),
    onSuccess: () => {
      // Invalidate pending invites and the user's clubs list. The clubs
      // queryKey is ['clubs'] (see useClubs.ts) — the previous invalidation
      // of ['user-clubs'] was dead since nothing reads from that key (#1572).
      queryClient.invalidateQueries({ queryKey: ['pending-invites'] });
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
    },
    successToast: 'Invite accepted! Welcome to the club.',
    oro: {
      op: OroOperation.AcceptInvite,
      retryPolicy: 'never',
      debugLabel: 'Accept invite button',
    },
  });
}

/**
 * Decline a pending invite
 */
export function useDeclineInvite() {
  const queryClient = useQueryClient();

  return useOroMutation<{ message: string }, string>({
    mutationFn: (code: string) => invitesApi.declineInvite(code),
    onSuccess: () => {
      // Invalidate pending invites
      queryClient.invalidateQueries({ queryKey: ['pending-invites'] });
    },
    successToast: 'Invite declined',
    oro: {
      op: OroOperation.DeclineInvite,
      retryPolicy: 'never',
      debugLabel: 'Decline invite button',
    },
  });
}
